import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PieChart, Pie, Cell, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { LeaveReportService } from '../../../services/leaveReportService';
import { OvertimeService } from '../../../services/overtimeService';
import { LeaveBalanceService } from '../../../services/leaveBalanceService';
import { LeavePolicyService } from '../../../services/leavePolicyService';
import { LeaveService } from '../../../services/leave';
// One shared definition of which accounts are administrative, kept with the roster service so
// Leave and Shift Roster cannot disagree about who counts as staff.
import { isAdministrativeRole } from '../../../services/shiftRoster';
import { CompOffService } from '../../../services/compOffService';
import { SimpleUser } from '../../../services/userLoader';
import { LeaveBalanceSnapshot, CompOffRecord, OvertimeRecord } from '../../../types/leavePolicy';
import { LeaveRequest } from '../../../types/leave';
import { LeavePageHeader, YearSelect } from './leaveShared';
import ReportDownloadMenu from './ReportDownloadMenu';
import {
  ExportColumn,
  exportReportCsv,
  exportReportPdf,
} from '../../../services/leaveReportExport';
import EmptyState from '../../common/EmptyState';
import { daysUntilExpiry, expirySummary, expiryTooltip, formatExpiryDate } from '../leaveExpiry';
import '../LeavePage.css';
import './leavePages.css';
import '../../common/emsPage.css';

// The shared definition — the Shift Roster asks the same question of the same field, and a
// second copy here is how the two would come to disagree about the same person.
import { hasLeftOrganisation as hasLeft } from '../../../services/employmentStatus';

type ReportType = 'EMP' | 'BALANCE' | 'COMPOFF' | 'OVERTIME';

/**
 * Who a report covers.
 *
 * PAST / CURRENT are derived from the employee record rather than from a new field: `dateOfExit`
 * is what User Management already writes when somebody leaves (and what leaveAccrualEngine already
 * uses to stop accruing for them), and Cognito's `enabled` flag covers an account disabled without
 * an exit date being filled in. Either one means "no longer here".
 */
type EmployeeTypeFilter = 'CURRENT' | 'PAST' | 'ALL' | 'SELECTED';

const EMPLOYEE_TYPE_LABELS: Record<EmployeeTypeFilter, string> = {
  CURRENT: 'Current Employees',
  PAST: 'Past Employees',
  ALL: 'All Employees',
  SELECTED: 'Selected Employees',
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* ────────────────────────────────────────────────────────────────────────────
 * Palette
 *
 * Every set below was checked with the data-viz validator against this page's
 * real surfaces (#ffffff light, #1e293b dark) rather than picked by eye:
 *
 *  - Leave types (6 slots, adjacent pairlist): passes lightness band, chroma
 *    floor, CVD separation and the normal-vision floor in both modes.
 *  - Trend series (3 slots, all-pairs): passes both modes. Amber was rejected
 *    for "Pending" because amber↔red hard-failed the dark normal-vision floor
 *    (ΔE 13.0 < 15); blue clears it at 29.0. Red↔green still sits in the CVD
 *    warn band in light mode, so the chart carries secondary encoding — a
 *    dashed line and distinct markers — and never leans on hue alone.
 *
 * Contrast warnings on the lighter slots are answered by the relief rule: every
 * chart here ships a legend, hover tooltips and a table view of the same data.
 * ──────────────────────────────────────────────────────────────────────────── */
const LEAVE_TYPE_COLORS: Record<string, { light: string; dark: string }> = {
  EL: { light: '#1baf7a', dark: '#199e70' },
  CL: { light: '#2a78d6', dark: '#3987e5' },
  SL: { light: '#eda100', dark: '#c98500' },
  ML: { light: '#e87ba4', dark: '#d55181' },
  PL: { light: '#4a3aa7', dark: '#9085e9' },
  CO: { light: '#eb6834', dark: '#d95926' },
};
/** Slots for any leave type beyond the six known codes, in validated order. */
const FALLBACK_SERIES = [
  { light: '#2a78d6', dark: '#3987e5' },
  { light: '#eb6834', dark: '#d95926' },
  { light: '#1baf7a', dark: '#199e70' },
  { light: '#eda100', dark: '#c98500' },
  { light: '#e87ba4', dark: '#d55181' },
  { light: '#008300', dark: '#008300' },
];

const TREND_COLORS = {
  approved: { light: '#008300', dark: '#008300' },
  pending:  { light: '#2a78d6', dark: '#3987e5' },
  rejected: { light: '#e34948', dark: '#e66767' },
};

/** Granted / Used / Remaining — one hue's ramp, so the three read as one measure
 *  split three ways rather than three unrelated categories. */
const STACK_COLORS = {
  used:      { light: '#1c5cab', dark: '#3987e5' },
  remaining: { light: '#86b6ef', dark: '#184f95' },
};

/** Sequential blue ramp for the heatmap (magnitude → one hue, light to dark). */
const HEAT_RAMP_LIGHT = ['#eef4fb', '#cde2fb', '#9ec5f4', '#5598e7', '#2a78d6', '#1c5cab'];
const HEAT_RAMP_DARK  = ['#16202e', '#104281', '#184f95', '#256abf', '#3987e5', '#6da7ec'];

const INK = {
  muted: { light: '#898781', dark: '#94a3b8' },
  grid:  { light: '#e1e0d9', dark: '#2c2c2a' },
};

/** Watches the app's theme stamp so chart colours (which can't be CSS variables
 *  inside an SVG library) follow the toggle. */
const useThemeMode = (): 'light' | 'dark' => {
  const read = (): 'light' | 'dark' =>
    document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  const [mode, setMode] = useState<'light' | 'dark'>(read);
  useEffect(() => {
    const obs = new MutationObserver(() => setMode(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return mode;
};

const round1 = (n: number) => Math.round(n * 10) / 10;
/**
 * Days used for one leave type, tolerating a missing snapshot.
 *
 * `snapshot.consumed` only counts requests raised in the app; leave taken before the system
 * went live was reconciled as a negative HR adjustment and is invisible to it. Every per-type
 * figure on this page reads through here so the charts, the table and the export cannot end up
 * measuring three different things.
 */
const usedOf = (s?: LeaveBalanceSnapshot): number => (s ? LeaveBalanceService.totalUsed(s) : 0);
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

interface EmpRow {
  user: SimpleUser;
  byType: Record<string, LeaveBalanceSnapshot>;
  granted: number;
  consumed: number;
  pending: number;
  balance: number;
}

/* ── KPI tile ─────────────────────────────────────────────────────────────── */
interface KpiProps {
  label: string;
  value: string | number;
  sub?: string;
  /** Accent colour for the value. The stat is identified by its label; the tint
   *  is a quiet secondary cue, never the only thing distinguishing one from another. */
  tone: 'teal' | 'green' | 'blue' | 'amber' | 'red' | 'violet';
  /** Month-over-month change. Omitted entirely when it cannot be computed from
   *  real data — a fabricated "+12%" would be worse than no chip at all. */
  delta?: number | null;
  deltaLabel?: string;
  index: number;
}

/**
 * One cell of the summary strip.
 *
 * Deliberately *not* a card: the page already stacks boxed charts and a boxed
 * table, and eight more bordered tiles on top read as boxes inside boxes. These
 * are plain stats on the page plane, separated by hairlines.
 */
const Kpi: React.FC<KpiProps> = ({ label, value, sub, tone, delta, deltaLabel, index }) => (
  <div className="lr-stat" style={{ ['--lr-i' as string]: index }}>
    <span className="lr-stat-head">
      <span className={`lr-stat-value lr-stat-value--${tone}`}>{value}</span>
      {typeof delta === 'number' && (
        <span className={`lr-stat-delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'}`}>
          {delta > 0 ? '▲' : delta < 0 ? '▼' : '■'}{Math.abs(delta)}%
        </span>
      )}
    </span>
    <span className="lr-stat-label">{label}</span>
    <span className="lr-stat-sub">{deltaLabel || sub || ''}</span>
  </div>
);

/* ── Chart shell ──────────────────────────────────────────────────────────── */
/**
 * `action` is where each section's Download control goes.
 *
 * It is a slot rather than the export data itself for the same reason ReportDownloadMenu takes
 * callbacks: a chart knows what its own rows mean, and threading every section's columns through
 * this shell would make it the one place every report's shape has to be described.
 */
const ChartCard: React.FC<{
  title: string; subtitle?: string; span?: 'half' | 'full'; children: React.ReactNode; index?: number;
  action?: React.ReactNode;
}> = ({ title, subtitle, span = 'half', children, index = 0, action }) => (
  <section className={`lr-chart-card lr-chart-card--${span}`} style={{ ['--lr-i' as string]: index }}>
    <header className="lr-chart-head">
      <div className="lr-chart-head-text">
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </header>
    <div className="lr-chart-body">{children}</div>
  </section>
);

const NoData: React.FC<{ msg: string; hint?: string }> = ({ msg, hint }) => (
  <div className="lr-nodata">
    <span className="lr-nodata-icon" aria-hidden="true">📊</span>
    <strong>{msg}</strong>
    {hint && <span>{hint}</span>}
  </div>
);

/* ════════════════════════════════════════════════════════════════════════════ */

const LeaveReportsPage: React.FC = () => {
  const mode = useThemeMode();
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [reportType, setReportType] = useState<ReportType>('EMP');

  const [rows, setRows] = useState<EmpRow[]>([]);
  const [allLeaves, setAllLeaves] = useState<LeaveRequest[]>([]);
  const [compOffRows, setCompOffRows] = useState<CompOffRecord[]>([]);
  const [otRows, setOtRows] = useState<OvertimeRecord[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState('');
  /*
   * Employee Type — who the report covers, independent of the department/team filters.
   *
   * SELECTED is the only option carrying extra state (the chosen emails), which is why it is a
   * separate mode rather than "an empty selection means everyone": an empty selection under
   * SELECTED means the admin has not picked anyone yet, and showing the whole company for it
   * would be the opposite of what they asked for.
   */
  const [employeeType, setEmployeeType] = useState<EmployeeTypeFilter>('CURRENT');
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [empPickerOpen, setEmpPickerOpen] = useState(false);
  const [empPickerSearch, setEmpPickerSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('ALL');
  // Team and employment type are recorded per employee in User Management, so they filter
  // here for the same reason department does: a report is normally read for one slice of the
  // company, and without them the only way to narrow it was by typing names one at a time.
  const [teamFilter, setTeamFilter] = useState('ALL');
  const [employmentFilter, setEmploymentFilter] = useState('ALL');
  const [sortKey, setSortKey] = useState<'name' | 'used' | 'balance'>('used');

  /** Active leave types drive every per-type chart, so a newly-added type (e.g.
   *  Paternity) appears automatically instead of needing a hardcoded column. */
  const leaveTypes = useMemo(
    () => LeavePolicyService.getLeaveTypes().filter(t => t.isActive),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialLoading, refreshing]
  );

  const colorFor = useCallback((code: string, i: number) => {
    const slot = LEAVE_TYPE_COLORS[code.toUpperCase()] || FALLBACK_SERIES[i % FALLBACK_SERIES.length];
    return slot[mode];
  }, [mode]);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // getBalancesForAll runs the same computation the Employee Profile uses, for
      // every employee in the directory — so these figures cannot drift from the
      // profile, the DOJ table or HR Operations.
      const [balances, leaves] = await Promise.all([
        LeaveBalanceService.getBalancesForAll(year),
        LeaveService.listLeaves(),
        // Overtime is read synchronously below, so its cache is refreshed here. Comp-Off and
        // the adjustment ledger are already refreshed inside getBalancesForAll.
        OvertimeService.refresh(),
      ]);
      /*
       * Administrative logins are excluded, matching Leave Assignment and the Shift Roster.
       *
       * Super_Admin, HR_Admin and Finance_Admin are operator accounts that accrue nothing, so they
       * were padding every headcount and every "employees with no balance" total on this page with
       * rows of zeros. One shared rule across the modules, so no two screens count staff
       * differently.
       */
      setRows(balances
        .filter(({ user }) => !isAdministrativeRole(user.role))
        .map(({ user, snapshots }) => {
        const byType: Record<string, LeaveBalanceSnapshot> = {};
        snapshots.forEach(s => { byType[s.leaveTypeCode.toUpperCase()] = s; });
        return {
          user,
          byType,
          // totalEntitlement, not bare `granted`: the balance summed below already carries
          // comp-off and HR grants, so totalling the raw grant here made "Granted" and
          // "Balance" describe different pools on the same row — and made this page
          // disagree with the employee's own dashboard about the same person.
          granted:  snapshots.reduce((a, s) => a + LeaveBalanceService.totalEntitlement(s), 0),
          // totalUsed, not the bare `consumed` field: the system went live mid-year, so leave
          // taken before then is a negative HR adjustment rather than a leave request. Summing
          // `consumed` reported nil utilisation for anyone whose leave predated go-live, which
          // fed every KPI, the distribution chart and the top-consumers list on this page.
          consumed: snapshots.reduce((a, s) => a + LeaveBalanceService.totalUsed(s), 0),
          pending:  snapshots.reduce((a, s) => a + s.pending, 0),
          balance:  snapshots.reduce((a, s) => a + s.balance, 0),
        };
      })
      );
      setAllLeaves(leaves);
      setCompOffRows(LeaveReportService.getCompOffReport());
      setOtRows(LeaveReportService.getOvertimeReport());
    } finally {
      setRefreshing(false);
      setInitialLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  // Every mutation elsewhere in the Leave module refreshes this page, so the
  // dashboard can never show figures the rest of the app has moved past.
  useEffect(() => {
    const evts = ['leave-balance-updated', 'leave-requests-updated', 'leave-policy-updated', 'storage'];
    evts.forEach(e => window.addEventListener(e, load));
    return () => { evts.forEach(e => window.removeEventListener(e, load)); };
  }, [load]);

  const yearLeaves = useMemo(
    () => allLeaves.filter(l => new Date(l.startDate).getFullYear() === year),
    [allLeaves, year]
  );

  const statusOf = (l: LeaveRequest) => String(l.status).toUpperCase();
  const isApproved = (l: LeaveRequest) => statusOf(l) === 'APPROVED';
  const isPending  = (l: LeaveRequest) => statusOf(l) === 'SUBMITTED' || statusOf(l) === 'PENDING';
  const isRejected = (l: LeaveRequest) => statusOf(l) === 'REJECTED';

  /* ── KPIs ───────────────────────────────────────────────────────────────── */
  const kpis = useMemo(() => {
    const totalGranted  = rows.reduce((a, r) => a + r.granted, 0);
    const totalConsumed = rows.reduce((a, r) => a + r.consumed, 0);
    const totalBalance  = rows.reduce((a, r) => a + r.balance, 0);
    const withDoj = rows.filter(r => !Object.values(r.byType)[0]?.dojMissing);

    const coEarned = compOffRows
      .filter(c => new Date(c.workedDate || c.createdAt).getFullYear() === year && c.status === 'APPROVED')
      .reduce((a, c) => a + c.daysGranted, 0);
    const otHours = otRows
      .filter(o => new Date(o.date).getFullYear() === year && o.status === 'APPROVED')
      .reduce((a, o) => a + o.hoursWorked, 0);

    // Month-over-month, computed from real request dates. Only reported for the
    // three request-status tiles, where the underlying data actually carries a
    // date; headcount and balance have no historical series to compare against.
    const now = new Date();
    const thisM = now.getMonth(), thisY = now.getFullYear();
    const prevDate = new Date(thisY, thisM - 1, 1);
    const inMonth = (l: LeaveRequest, m: number, y: number) => {
      const d = new Date(l.startDate);
      return d.getMonth() === m && d.getFullYear() === y;
    };
    const delta = (fn: (l: LeaveRequest) => boolean): number | null => {
      if (year !== thisY) return null; // comparing months of a past year to "now" is meaningless
      const cur = allLeaves.filter(l => fn(l) && inMonth(l, thisM, thisY)).length;
      const prv = allLeaves.filter(l => fn(l) && inMonth(l, prevDate.getMonth(), prevDate.getFullYear())).length;
      if (prv === 0) return cur === 0 ? 0 : null; // no baseline → no honest percentage
      return Math.round(((cur - prv) / prv) * 100);
    };

    return {
      employees: rows.length,
      approved: yearLeaves.filter(isApproved).length,
      pending:  yearLeaves.filter(isPending).length,
      rejected: yearLeaves.filter(isRejected).length,
      utilization: pct(totalConsumed, totalGranted),
      avgBalance: withDoj.length ? round1(totalBalance / withDoj.length) : 0,
      coEarned: round1(coEarned),
      otHours: round1(otHours),
      dApproved: delta(isApproved),
      dPending:  delta(isPending),
      dRejected: delta(isRejected),
    };
  }, [rows, yearLeaves, allLeaves, compOffRows, otRows, year]);

  /* ── Section 2: distribution + trend ────────────────────────────────────── */
  const distribution = useMemo(() => {
    const out = leaveTypes.map((t, i) => {
      const code = t.code.toUpperCase();
      const value = rows.reduce((a, r) => a + usedOf(r.byType[code]), 0);
      return { name: t.name, full: t.name, value: round1(value), color: colorFor(code, i) };
    }).filter(d => d.value > 0);
    const total = out.reduce((a, d) => a + d.value, 0);
    return { data: out, total: round1(total) };
  }, [rows, leaveTypes, colorFor]);

  const monthlyTrend = useMemo(() => {
    const mk = () => new Array(12).fill(0);
    const a = mk(), p = mk(), r = mk();
    yearLeaves.forEach(l => {
      const m = new Date(l.startDate).getMonth();
      if (isApproved(l)) a[m] += 1;
      else if (isPending(l)) p[m] += 1;
      else if (isRejected(l)) r[m] += 1;
    });
    return MONTH_LABELS.map((month, i) => ({ month, Approved: a[i], Pending: p[i], Rejected: r[i] }));
  }, [yearLeaves]);

  const trendHasData = useMemo(
    () => monthlyTrend.some(m => m.Approved || m.Pending || m.Rejected),
    [monthlyTrend]
  );

  /* ── Section 3: departments ─────────────────────────────────────────────── */
  const departmentUsage = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach(r => {
      const dept = r.user.department || 'Unassigned';
      map.set(dept, (map.get(dept) || 0) + r.consumed);
    });
    return Array.from(map.entries())
      .map(([department, days]) => ({ department, days: round1(days) }))
      .filter(d => d.days > 0)
      .sort((a, b) => b.days - a.days)
      .slice(0, 8);
  }, [rows]);

  /* ── Section 4: granted vs used vs remaining ────────────────────────────── */
  const stacked = useMemo(() => leaveTypes.map(t => {
    const code = t.code.toUpperCase();
    // Same pool as the row totals above, so "Remaining" here matches the Balance column
    // rather than understating it by whatever HR granted.
    const granted  = rows.reduce((a, r) => {
      const s = r.byType[code];
      return a + (s ? LeaveBalanceService.totalEntitlement(s) : 0);
    }, 0);
    const used     = rows.reduce((a, r) => a + usedOf(r.byType[code]), 0);
    return {
      type: t.name,
      Used: round1(used),
      Remaining: round1(Math.max(0, granted - used)),
      granted: round1(granted),
    };
  }).filter(d => d.granted > 0 || d.Used > 0), [rows, leaveTypes]);

  /* ── Section 5: top consumers ───────────────────────────────────────────── */
  const topConsumers = useMemo(() => {
    const list = rows
      .map(r => ({
        name: r.user.fullName || r.user.name,
        dept: r.user.department || '—',
        days: round1(r.consumed),
      }))
      .filter(r => r.days > 0)
      .sort((a, b) => b.days - a.days)
      .slice(0, 8);
    const max = list.length ? list[0].days : 0;
    return { list, max };
  }, [rows]);

  /* ── Section 6: activity heatmap ────────────────────────────────────────── */
  const heatmap = useMemo(() => {
    // Count approved+pending leave-days landing on each calendar date.
    const counts = new Map<string, number>();
    yearLeaves.filter(l => isApproved(l) || isPending(l)).forEach(l => {
      const start = new Date(l.startDate), end = new Date(l.endDate);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().slice(0, 10);
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    });
    const max = Math.max(0, ...Array.from(counts.values()));
    const months = MONTH_LABELS.map((label, m) => {
      const days = new Date(year, m + 1, 0).getDate();
      // Lead-in blanks so each column is a real Sun→Sat week: without them the
      // seven rows wouldn't line up with weekdays and the weekend shading would
      // read as an arbitrary stripe.
      const lead = new Date(year, m, 1).getDay();
      const blanks = Array.from({ length: lead }, (_, i) => ({
        key: `pad-${m}-${i}`, day: 0, count: -1, weekend: false,
      }));
      const cells = Array.from({ length: days }, (_, i) => {
        const date = new Date(year, m, i + 1);
        const key = `${year}-${String(m + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
        return { key, day: i + 1, count: counts.get(key) || 0, weekend: date.getDay() === 0 || date.getDay() === 6 };
      });
      return { label, cells: [...blanks, ...cells] };
    });
    return { months, max, total: Array.from(counts.values()).reduce((a, b) => a + b, 0) };
  }, [yearLeaves, year]);

  const heatColor = useCallback((count: number) => {
    const ramp = mode === 'dark' ? HEAT_RAMP_DARK : HEAT_RAMP_LIGHT;
    if (count <= 0) return ramp[0];
    const step = Math.ceil((count / Math.max(1, heatmap.max)) * (ramp.length - 1));
    return ramp[Math.min(ramp.length - 1, Math.max(1, step))];
  }, [mode, heatmap.max]);

  /* ── Section 7: balance distribution ────────────────────────────────────── */
  const histogram = useMemo(() => {
    const buckets = [
      { range: '0–2',  min: 0,  max: 2 },
      { range: '3–5',  min: 3,  max: 5 },
      { range: '6–8',  min: 6,  max: 8 },
      { range: '9–12', min: 9,  max: 12 },
      { range: '13+',  min: 13, max: Infinity },
    ];
    return buckets.map(b => ({
      range: b.range,
      employees: rows.filter(r => r.balance >= b.min && r.balance <= b.max).length,
    }));
  }, [rows]);

  /* ── Table ──────────────────────────────────────────────────────────────── */
  const departments = useMemo(
    () => Array.from(new Set(rows.map(r => r.user.department).filter(Boolean))).sort(),
    [rows]
  );
  // Built from the loaded rows rather than the fixed TEAM_OPTIONS list, so the dropdown only
  // ever offers values that would actually match something.
  const teams = useMemo(
    () => Array.from(new Set(rows.map(r => r.user.team).filter(Boolean))).sort() as string[],
    [rows]
  );
  const employmentTypes = useMemo(
    () => Array.from(new Set(rows.map(r => r.user.employmentType).filter(Boolean))).sort() as string[],
    [rows]
  );

  /** Today, for the past/current split. Local, matching every other date decision in Leave. */
  const todayIso = CompOffService.todayIso();

  /**
   * The one filtered dataset.
   *
   * Every export derives from this array, not from a separate query and not from a page slice —
   * which is what makes "filters affect the exported data" true by construction rather than by
   * remembering to apply them twice.
   */
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const chosen = new Set(selectedEmails.map(e => e.toLowerCase()));
    return rows
      .filter(r => {
        switch (employeeType) {
          case 'CURRENT': return !hasLeft(r.user, todayIso);
          case 'PAST':    return hasLeft(r.user, todayIso);
          case 'SELECTED': return chosen.has((r.user.email || '').toLowerCase());
          default:        return true;
        }
      })
      .filter(r => deptFilter === 'ALL' || r.user.department === deptFilter)
      .filter(r => teamFilter === 'ALL' || (r.user.team || '') === teamFilter)
      .filter(r => employmentFilter === 'ALL' || (r.user.employmentType || '') === employmentFilter)
      .filter(r => !q
        || (r.user.fullName || r.user.name || '').toLowerCase().includes(q)
        || (r.user.email || '').toLowerCase().includes(q))
      .sort((a, b) => {
        if (sortKey === 'name') return (a.user.fullName || a.user.name).localeCompare(b.user.fullName || b.user.name);
        if (sortKey === 'balance') return b.balance - a.balance;
        return b.consumed - a.consumed;
      });
  }, [rows, search, deptFilter, teamFilter, employmentFilter, sortKey, employeeType, selectedEmails, todayIso]);

  /**
   * The employees the Comp-Off and Overtime reports may show.
   *
   * Those two are keyed by email rather than by an employee record, so the Employee Type filter has
   * to be translated into a set of addresses. Undefined means "no employee restriction" — under
   * ALL there is nothing to narrow by, and building a set of every address would only be a slower
   * way of saying the same thing.
   */
  const allowedEmails = useMemo((): Set<string> | undefined => {
    if (employeeType === 'ALL') return undefined;
    const source = employeeType === 'SELECTED'
      ? selectedEmails
      : rows
          .filter(r => (employeeType === 'PAST') === hasLeft(r.user, todayIso))
          .map(r => r.user.email);
    return new Set(source.map(e => (e || '').toLowerCase()));
  }, [employeeType, selectedEmails, rows, todayIso]);

  const inScope = useCallback(
    (email: string) => !allowedEmails || allowedEmails.has((email || '').toLowerCase()),
    [allowedEmails]
  );

  /** Comp-Off rows for the selected year, narrowed by the Employee Type filter. */
  const visibleCompOff = useMemo(
    () => compOffRows
      .filter(c => new Date(c.workedDate || c.createdAt).getFullYear() === year)
      .filter(c => inScope(c.employeeEmail)),
    [compOffRows, year, inScope]
  );

  /** Overtime rows for the selected year, narrowed the same way. */
  const visibleOvertime = useMemo(
    () => otRows
      .filter(o => new Date(o.date).getFullYear() === year)
      .filter(o => inScope(o.employeeEmail)),
    [otRows, year, inScope]
  );

  /** Employees offered in the Selected-Employees picker, searchable by name or email. */
  const pickerEmployees = useMemo(() => {
    const q = empPickerSearch.trim().toLowerCase();
    return rows
      .map(r => r.user)
      .filter(u => !q
        || (u.fullName || u.name || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q))
      .sort((a, b) => (a.fullName || a.name || '').localeCompare(b.fullName || b.name || ''));
  }, [rows, empPickerSearch]);

  const toggleSelected = useCallback((email: string) => {
    setSelectedEmails(prev =>
      prev.some(e => e.toLowerCase() === email.toLowerCase())
        ? prev.filter(e => e.toLowerCase() !== email.toLowerCase())
        : [...prev, email]
    );
  }, []);

  /**
   * The filters, spelled out for the exported file.
   *
   * Included in both CSV and PDF because an export outlives the screen: a reader who cannot tell
   * whether 12 rows is the whole company or one filtered team cannot interpret the file.
   */
  const exportMeta = useMemo(() => {
    const meta = [
      { label: 'Year', value: String(year) },
      { label: 'Employee Type', value: EMPLOYEE_TYPE_LABELS[employeeType] },
    ];
    if (employeeType === 'SELECTED') {
      meta.push({ label: 'Selected', value: selectedEmails.length ? selectedEmails.join('; ') : 'none' });
    }
    if (deptFilter !== 'ALL') meta.push({ label: 'Department', value: deptFilter });
    if (teamFilter !== 'ALL') meta.push({ label: 'Team', value: teamFilter });
    if (employmentFilter !== 'ALL') meta.push({ label: 'Employment Type', value: employmentFilter });
    if (search.trim()) meta.push({ label: 'Search', value: search.trim() });
    return meta;
  }, [year, employeeType, selectedEmails, deptFilter, teamFilter, employmentFilter, search]);

  /*
   * ── Exports ───────────────────────────────────────────────────────────────
   *
   * Each report describes its own columns once and both formats are generated from that one
   * description, so CSV and PDF cannot disagree. Every `rows` below is the same filtered array the
   * table renders — never a slice — which is what makes the filters reach the file.
   */
  const empColumns = useMemo((): ExportColumn<EmpRow>[] => [
    { header: 'Employee', value: r => r.user.fullName || r.user.name },
    { header: 'Email', value: r => r.user.email },
    { header: 'Employee ID', value: r => r.user.employeeId || '' },
    { header: 'Department', value: r => r.user.department || '' },
    { header: 'Team', value: r => r.user.team || '' },
    { header: 'Employment Type', value: r => r.user.employmentType || '' },
    { header: 'Status', value: r => (hasLeft(r.user, todayIso) ? 'Past' : 'Current') },
    { header: 'Date of Exit', value: r => r.user.dateOfExit || '' },
    // A column per active leave type, so a newly added type appears in the export automatically
    // rather than needing this list edited — the same reasoning the table header follows.
    ...leaveTypes.flatMap((t): ExportColumn<EmpRow>[] => [
      {
        header: `${t.name} — Allotment`,
        numeric: true,
        value: r => {
          const s = r.byType[t.code.toUpperCase()];
          return s ? LeaveBalanceService.totalEntitlement(s) : 0;
        },
      },
      {
        header: `${t.name} — Used`,
        numeric: true,
        value: r => usedOf(r.byType[t.code.toUpperCase()]),
      },
      {
        header: `${t.name} — Balance`,
        numeric: true,
        value: r => r.byType[t.code.toUpperCase()]?.balance ?? 0,
      },
    ]),
    { header: 'Total Allotment', numeric: true, value: r => round1(r.granted) },
    { header: 'Total Used', numeric: true, value: r => round1(r.consumed) },
    { header: 'Pending', numeric: true, value: r => round1(r.pending) },
    { header: 'Total Balance', numeric: true, value: r => round1(r.balance) },
  ], [leaveTypes, todayIso]);

  const compOffColumns = useMemo((): ExportColumn<CompOffRecord>[] => [
    { header: 'Employee', value: c => c.employeeEmail },
    { header: 'Worked Date', value: c => c.workedDate || '' },
    { header: 'Holiday', value: c => (c.isHoliday ? 'Yes' : 'No') },
    { header: 'Days', numeric: true, value: c => c.daysGranted },
    { header: 'Status', value: c => c.status },
    {
      header: 'Expires',
      // Derived, matching the table: the stored expiryDate can be stale because the window is a
      // live HR setting, and the balance maths ignores it for that reason.
      value: c => (c.status === 'APPROVED' ? CompOffService.expiryDateFor(c.workedDate) : ''),
    },
    { header: 'Granted By', value: c => c.grantedBy },
    { header: 'Decided By', value: c => c.decidedBy || '' },
    { header: 'Decided On', value: c => CompOffService.localDateOf(c.decidedAt) },
    { header: 'Reason', value: c => c.reason || '' },
  ], []);

  const overtimeColumns = useMemo((): ExportColumn<OvertimeRecord>[] => [
    { header: 'Employee', value: o => o.employeeEmail },
    { header: 'Date', value: o => o.date },
    { header: 'OT Type', value: o => o.otType },
    { header: 'Hours', numeric: true, value: o => o.hoursWorked },
    { header: 'Status', value: o => o.status },
  ], []);

  /** Title, columns and rows for whichever report tab is showing. */
  const activeReport = useMemo(() => {
    if (reportType === 'COMPOFF') {
      return { title: `Comp-Off Report ${year}`, columns: compOffColumns, rows: visibleCompOff };
    }
    if (reportType === 'OVERTIME') {
      return { title: `Overtime Report ${year}`, columns: overtimeColumns, rows: visibleOvertime };
    }
    return {
      title: `${reportType === 'EMP' ? 'Employee Summary' : 'Leave Utilization'} ${year}`,
      columns: empColumns,
      rows: visibleRows,
    };
  }, [reportType, year, empColumns, visibleRows, compOffColumns, visibleCompOff, overtimeColumns, visibleOvertime]);

  /** Download handlers for a report described by its own columns/rows. */
  const downloadCsvFor = useCallback(
    <T,>(title: string, columns: ExportColumn<T>[], rowsToExport: T[]) =>
      exportReportCsv({ title, columns, rows: rowsToExport, meta: exportMeta }),
    [exportMeta]
  );
  const downloadPdfFor = useCallback(
    <T,>(title: string, columns: ExportColumn<T>[], rowsToExport: T[]) =>
      exportReportPdf({ title, columns, rows: rowsToExport, meta: exportMeta }),
    [exportMeta]
  );

  /**
   * The Download control for one section, wired to that section's own columns and rows.
   *
   * Every chart on this page is a summary of numbers somebody eventually has to put in a
   * deck or reconcile in a spreadsheet, and until now only the bottom table could be taken
   * off the screen — the eight sections above it could only be read or screenshotted. This
   * exists so adding the affordance to a section is one call rather than eight copies of
   * the same JSX, which is what would have let the label, the format list and the row count
   * drift apart between cards.
   *
   * The rows handed in are always the array the section actually rendered, so an export
   * matches what the reader is looking at — including the year and any filters, which reach
   * the file through the shared `exportMeta`.
   */
  const sectionDownload = useCallback(
    <T,>(title: string, columns: ExportColumn<T>[], sectionRows: T[]) => (
      <ReportDownloadMenu
        label={title}
        rowCount={sectionRows.length}
        onCsv={() => downloadCsvFor(title, columns, sectionRows)}
        onPdf={() => downloadPdfFor(title, columns, sectionRows)}
      />
    ),
    [downloadCsvFor, downloadPdfFor]
  );

  /*
   * ── Per-section export shapes ─────────────────────────────────────────────
   *
   * Each is the chart's own data described as a table. Deliberately derived from the exact
   * memo the chart renders rather than recomputed from `rows`: a second derivation is how a
   * downloaded figure ends up disagreeing with the one on screen.
   */
  const kpiExport = useMemo(
    () => [
      { metric: 'Total Employees', value: kpis.employees, detail: 'In directory' },
      { metric: 'Approved Leaves', value: kpis.approved, detail: kpis.dApproved != null ? `${kpis.dApproved}% vs last month` : `Requests in ${year}` },
      { metric: 'Pending Requests', value: kpis.pending, detail: kpis.dPending != null ? `${kpis.dPending}% vs last month` : 'Awaiting decision' },
      { metric: 'Rejected Leaves', value: kpis.rejected, detail: kpis.dRejected != null ? `${kpis.dRejected}% vs last month` : `Requests in ${year}` },
      { metric: 'Leave Utilization', value: `${kpis.utilization}%`, detail: 'Used of granted' },
      { metric: 'Avg Leave Balance', value: kpis.avgBalance, detail: 'Days per employee' },
      { metric: 'Comp-Off Earned', value: kpis.coEarned, detail: `Days in ${year}` },
      { metric: 'Overtime Hours', value: kpis.otHours, detail: `Approved in ${year}` },
    ],
    [kpis, year]
  );

  const kpiColumns = useMemo(
    (): ExportColumn<{ metric: string; value: string | number; detail: string }>[] => [
      { header: 'Metric', value: k => k.metric },
      { header: 'Value', value: k => k.value },
      { header: 'Detail', value: k => k.detail },
    ],
    []
  );

  const distributionColumns = useMemo(
    (): ExportColumn<{ name: string; value: number }>[] => [
      { header: 'Leave Type', value: d => d.name },
      { header: 'Days Taken', numeric: true, value: d => d.value },
      // The percentage is on the chart's own labels, so leaving it out of the file would make
      // the two disagree about what the reader was shown.
      { header: 'Share %', numeric: true, value: d => pct(d.value, distribution.total) },
    ],
    [distribution.total]
  );

  const trendColumns = useMemo(
    (): ExportColumn<{ month: string; Approved: number; Pending: number; Rejected: number }>[] => [
      { header: 'Month', value: m => m.month },
      { header: 'Approved', numeric: true, value: m => m.Approved },
      { header: 'Pending', numeric: true, value: m => m.Pending },
      { header: 'Rejected', numeric: true, value: m => m.Rejected },
    ],
    []
  );

  const departmentColumns = useMemo(
    (): ExportColumn<{ department: string; days: number }>[] => [
      { header: 'Department', value: d => d.department },
      { header: 'Days Taken', numeric: true, value: d => d.days },
    ],
    []
  );

  const stackedColumns = useMemo(
    (): ExportColumn<{ type: string; Used: number; Remaining: number; granted: number }>[] => [
      { header: 'Leave Type', value: s => s.type },
      { header: 'Allotment', numeric: true, value: s => s.granted },
      { header: 'Used', numeric: true, value: s => s.Used },
      { header: 'Remaining', numeric: true, value: s => s.Remaining },
    ],
    []
  );

  const consumerColumns = useMemo(
    (): ExportColumn<{ name: string; dept: string; days: number }>[] => [
      // The rank is what the chart communicates and it is not a field on the row, so it comes
      // from the array order — the same order the list is rendered in.
      { header: 'Rank', numeric: true, value: e => topConsumers.list.indexOf(e) + 1 },
      { header: 'Employee', value: e => e.name },
      { header: 'Department', value: e => e.dept },
      { header: 'Days Taken', numeric: true, value: e => e.days },
    ],
    [topConsumers.list]
  );

  const histogramColumns = useMemo(
    (): ExportColumn<{ range: string; employees: number }>[] => [
      { header: 'Balance Range (days)', value: h => h.range },
      { header: 'Employees', numeric: true, value: h => h.employees },
    ],
    []
  );

  /**
   * The heatmap as one row per day that had anybody on leave.
   *
   * Padding cells (count -1) and quiet days are dropped: a 365-row file that is mostly zeroes
   * buries the handful of dates the chart exists to show, and the omitted days are recoverable
   * from the dates that are present.
   */
  const heatmapExport = useMemo(
    () =>
      heatmap.months.flatMap(m =>
        m.cells
          .filter(c => c.count > 0)
          .map(c => ({ date: c.key, month: m.label, count: c.count }))
      ),
    [heatmap.months]
  );

  const heatmapColumns = useMemo(
    (): ExportColumn<{ date: string; month: string; count: number }>[] => [
      { header: 'Date', value: c => c.date },
      { header: 'Month', value: c => c.month },
      { header: 'Employees On Leave', numeric: true, value: c => c.count },
    ],
    []
  );

  const initials = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

  const axis = { stroke: INK.muted[mode], fontSize: 11 };
  const tooltipStyle = {
    background: mode === 'dark' ? '#0f172a' : '#fff',
    border: `1px solid ${mode === 'dark' ? '#334155' : '#e2e8f0'}`,
    borderRadius: 10,
    fontSize: 12,
    color: mode === 'dark' ? '#f1f5f9' : '#0f172a',
    boxShadow: '0 8px 24px rgba(15,23,42,.12)',
  };

  if (initialLoading) {
    return (
      <section className="leave-page lr-page">
        <LeavePageHeader title={`Leave Reports (${year})`} actions={<YearSelect value={year} onChange={setYear} />} />
        <div className="lr-stat-strip">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="lr-stat">
              <span className="lr-skel-line lr-skel-stat-val" />
              <span className="lr-skel-line lr-skel-stat-lbl" />
            </div>
          ))}
        </div>
        <div className="lr-chart-grid">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="lr-chart-card lr-chart-card--half">
              <div className="lr-skel-line lr-skel-title" />
              <div className="lr-skel-block" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  const noEmployees = rows.length === 0;

  return (
    <section className="leave-page lr-page">
      <LeavePageHeader
        title={`Leave Reports (${year})`}
        actions={<YearSelect value={year} onChange={setYear} />}
      />

      {noEmployees ? (
        <EmptyState
          icon="inbox"
          title="No employee records available"
          subtitle="Once employees exist in User Management with a Date of Joining, their leave analytics appear here."
        />
      ) : (
        <div className={refreshing ? 'lr-refreshing' : undefined}>
          {/* ── 1 · Summary strip ── */}
          {/* The strip carries no card of its own, so its Download needs a header to sit in —
              placed here rather than inside a tile so the eight figures stay a single block. */}
          <div className="lr-section-head">
            <h3>Summary</h3>
            {sectionDownload(`Leave Summary ${year}`, kpiColumns, kpiExport)}
          </div>
          <div className="lr-stat-strip">
            <Kpi index={0} tone="teal"   label="Total Employees"   value={kpis.employees} sub="In directory" />
            <Kpi index={1} tone="green"  label="Approved Leaves"   value={kpis.approved} delta={kpis.dApproved} deltaLabel={kpis.dApproved != null ? 'vs last month' : `Requests in ${year}`} />
            <Kpi index={2} tone="blue"   label="Pending Requests"  value={kpis.pending}  delta={kpis.dPending}  deltaLabel={kpis.dPending != null ? 'vs last month' : 'Awaiting decision'} />
            <Kpi index={3} tone="red"    label="Rejected Leaves"   value={kpis.rejected} delta={kpis.dRejected} deltaLabel={kpis.dRejected != null ? 'vs last month' : `Requests in ${year}`} />
            <Kpi index={4} tone="amber"  label="Leave Utilization" value={`${kpis.utilization}%`} sub="Used of granted" />
            <Kpi index={5} tone="teal"   label="Avg Leave Balance" value={kpis.avgBalance} sub="Days per employee" />
            <Kpi index={6} tone="violet" label="Comp-Off Earned"   value={kpis.coEarned} sub={`Days in ${year}`} />
            <Kpi index={7} tone="violet" label="Overtime Hours"    value={kpis.otHours}  sub={`Approved in ${year}`} />
          </div>

          {/* ── 2 · Main analytics ── */}
          <div className="lr-chart-grid">
            <ChartCard
              index={0}
              title="Leave Distribution"
              subtitle="Days taken by leave type"
              action={sectionDownload(`Leave Distribution ${year}`, distributionColumns, distribution.data)}
            >
              {distribution.data.length === 0 ? (
                <NoData msg="No leave taken yet" hint={`Nothing has been consumed in ${year}.`} />
              ) : (
                <div className="lr-donut-wrap">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={distribution.data} dataKey="value" nameKey="name"
                        cx="50%" cy="50%" innerRadius={72} outerRadius={110} paddingAngle={2}
                        stroke={mode === 'dark' ? '#1e293b' : '#fff'} strokeWidth={2}
                        label={(d: any) => `${d.name} ${pct(d.value, distribution.total)}%`}
                        labelLine={false}
                        animationDuration={600}
                      >
                        {distribution.data.map(d => <Cell key={d.name} fill={d.color} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(v: any, n: any) => [`${v} days (${pct(Number(v), distribution.total)}%)`, n]}
                      />
                      <Legend verticalAlign="bottom" height={28} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="lr-donut-center">
                    <span className="lr-donut-total">{distribution.total}</span>
                    <span className="lr-donut-cap">days taken</span>
                  </div>
                </div>
              )}
            </ChartCard>

            <ChartCard
              index={1}
              title="Monthly Leave Trend"
              subtitle="Requests by status across the year"
              // Twelve rows always, even in a quiet year: a month with no requests is a fact the
              // reader needs, and dropping it would make the file look like a partial extract.
              action={sectionDownload(`Monthly Leave Trend ${year}`, trendColumns, monthlyTrend)}
            >
              {!trendHasData ? (
                <NoData msg="No requests yet" hint={`No leave was requested in ${year}.`} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyTrend} margin={{ top: 8, right: 12, bottom: 4, left: -12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={INK.grid[mode]} vertical={false} />
                    <XAxis dataKey="month" tick={axis} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={axis} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
                    {/* Distinct markers + a dashed line for Rejected: red↔green sits in
                        the CVD warn band, so identity never rests on hue alone. */}
                    <Line type="monotone" dataKey="Approved" stroke={TREND_COLORS.approved[mode]} strokeWidth={2}
                          dot={{ r: 4, strokeWidth: 0 }} activeDot={{ r: 6 }} animationDuration={700} />
                    <Line type="monotone" dataKey="Pending" stroke={TREND_COLORS.pending[mode]} strokeWidth={2}
                          dot={{ r: 4, strokeWidth: 0 }} activeDot={{ r: 6 }} animationDuration={700} />
                    <Line type="monotone" dataKey="Rejected" stroke={TREND_COLORS.rejected[mode]} strokeWidth={2}
                          strokeDasharray="6 3" dot={{ r: 4, strokeWidth: 0 }} activeDot={{ r: 6 }} animationDuration={700} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* ── 3 + 4 ── */}
          <div className="lr-chart-grid">
            <ChartCard
              index={2}
              title="Department-wise Leave Usage"
              subtitle="Days taken, highest first"
              action={sectionDownload(`Department Leave Usage ${year}`, departmentColumns, departmentUsage)}
            >
              {departmentUsage.length === 0 ? (
                <NoData msg="No department usage yet" />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, departmentUsage.length * 38)}>
                  <BarChart data={departmentUsage} layout="vertical" margin={{ top: 4, right: 28, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={INK.grid[mode]} horizontal={false} />
                    <XAxis type="number" tick={axis} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="department" width={130} tick={axis} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: mode === 'dark' ? '#ffffff0d' : '#0f172a08' }}
                             formatter={(v: any) => [`${v} days`, 'Leave taken']} />
                    <Bar dataKey="days" fill={LEAVE_TYPE_COLORS.CL[mode]} radius={[0, 4, 4, 0]} barSize={18} animationDuration={650} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard
              index={3}
              title="Allotment vs Usage"
              subtitle="Used and remaining days per leave type"
              action={sectionDownload(`Allotment vs Usage ${year}`, stackedColumns, stacked)}
            >
              {stacked.length === 0 ? (
                <NoData msg="No allotment yet" hint="Set a Date of Joining so leave starts accruing." />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, stacked.length * 46)}>
                  <BarChart data={stacked} layout="vertical" margin={{ top: 4, right: 28, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={INK.grid[mode]} horizontal={false} />
                    <XAxis type="number" tick={axis} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="type" width={54} tick={axis} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: mode === 'dark' ? '#ffffff0d' : '#0f172a08' }} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
                    {/* 2px surface gap between the stacked segments (stroke in the card colour). */}
                    <Bar dataKey="Used" stackId="a" fill={STACK_COLORS.used[mode]} barSize={20}
                         stroke={mode === 'dark' ? '#1e293b' : '#fff'} strokeWidth={2} animationDuration={650} />
                    <Bar dataKey="Remaining" stackId="a" fill={STACK_COLORS.remaining[mode]} radius={[0, 4, 4, 0]} barSize={20}
                         stroke={mode === 'dark' ? '#1e293b' : '#fff'} strokeWidth={2} animationDuration={650} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* ── 5 + 7 ── */}
          <div className="lr-chart-grid">
            <ChartCard
              index={4}
              title="Top Leave Consumers"
              subtitle="Employees by days taken"
              // The chart's own top-8 list, not every employee: the download has to be the
              // section, and the full per-employee figures are the Employee Summary report below.
              action={sectionDownload(`Top Leave Consumers ${year}`, consumerColumns, topConsumers.list)}
            >
              {topConsumers.list.length === 0 ? (
                <NoData msg="No leave taken yet" />
              ) : (
                <ol className="lr-rank">
                  {topConsumers.list.map((e, i) => (
                    <li key={e.name + i} className="lr-rank-row" style={{ ['--lr-i' as string]: i }}>
                      <span className={`lr-rank-pos lr-rank-pos--${i < 3 ? i + 1 : 'n'}`}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                      </span>
                      <span className="lr-avatar" aria-hidden="true">{initials(e.name)}</span>
                      <span className="lr-rank-id">
                        <strong>{e.name}</strong>
                        <small>{e.dept}</small>
                      </span>
                      <span className="lr-rank-bar">
                        <span
                          className="lr-rank-fill"
                          style={{ width: `${pct(e.days, topConsumers.max)}%`, background: LEAVE_TYPE_COLORS.EL[mode] }}
                        />
                      </span>
                      <span className="lr-rank-val">{e.days}<small>d</small></span>
                    </li>
                  ))}
                </ol>
              )}
            </ChartCard>

            <ChartCard
              index={5}
              title="Leave Balance Distribution"
              subtitle="How many employees sit in each balance band"
              action={sectionDownload(`Leave Balance Distribution ${year}`, histogramColumns, histogram)}
            >
              {rows.length === 0 ? <NoData msg="No employees" /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={histogram} margin={{ top: 8, right: 12, bottom: 4, left: -12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={INK.grid[mode]} vertical={false} />
                    <XAxis dataKey="range" tick={axis} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={axis} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: mode === 'dark' ? '#ffffff0d' : '#0f172a08' }}
                             formatter={(v: any) => [`${v} employees`, 'Count']} />
                    <Bar dataKey="employees" fill={LEAVE_TYPE_COLORS.EL[mode]} radius={[4, 4, 0, 0]} barSize={44} animationDuration={650} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* ── 6 · Heatmap ── */}
          <ChartCard
            index={6}
            span="full"
            title="Leave Activity Calendar"
            subtitle={`Employees on leave per day · ${heatmap.total} total leave-days in ${year}`}
            action={sectionDownload(`Leave Activity Calendar ${year}`, heatmapColumns, heatmapExport)}
          >
            {heatmap.max === 0 ? (
              <NoData msg="No leave activity" hint={`Nothing scheduled in ${year}.`} />
            ) : (
              <>
                <div className="lr-heat">
                  {heatmap.months.map(m => (
                    <div key={m.label} className="lr-heat-month">
                      <span className="lr-heat-label">{m.label}</span>
                      <div className="lr-heat-cells">
                        {m.cells.map(c => (
                          c.count < 0
                            ? <span key={c.key} className="lr-heat-cell is-pad" aria-hidden="true" />
                            : (
                              <span
                                key={c.key}
                                className={`lr-heat-cell${c.weekend ? ' is-weekend' : ''}`}
                                style={{ background: heatColor(c.count) }}
                                title={`${c.key} · ${c.count} ${c.count === 1 ? 'person' : 'people'} on leave`}
                              />
                            )
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="lr-heat-legend">
                  <span>Less</span>
                  {(mode === 'dark' ? HEAT_RAMP_DARK : HEAT_RAMP_LIGHT).map(c => (
                    <span key={c} className="lr-heat-key" style={{ background: c }} />
                  ))}
                  <span>More</span>
                </div>
              </>
            )}
          </ChartCard>

          {/* ── Detailed reports table ── */}
          <div className="lr-table-card">
            <div className="lr-table-toolbar">
              <div className="lb-modal-tabs">
                {([['EMP', 'Employee Summary'], ['BALANCE', 'Leave Utilization'], ['COMPOFF', 'Comp-Off'], ['OVERTIME', 'Overtime']] as const)
                  .map(([k, label]) => (
                    <button key={k} className={`lb-tab-btn ${reportType === k ? 'active' : ''}`} onClick={() => setReportType(k as ReportType)}>
                      {label}
                    </button>
                  ))}
              </div>
              {/* Download sits on the toolbar for every report tab, so the affordance does not move
                  as the admin switches between them. */}
              <div className="lr-toolbar-actions">
                <ReportDownloadMenu
                  label={activeReport.title}
                  rowCount={activeReport.rows.length}
                  onCsv={() => downloadCsvFor(activeReport.title, activeReport.columns as ExportColumn<unknown>[], activeReport.rows as unknown[])}
                  onPdf={() => downloadPdfFor(activeReport.title, activeReport.columns as ExportColumn<unknown>[], activeReport.rows as unknown[])}
                />
              </div>

              {/* Employee Type applies to every report, not just the two employee-shaped ones —
                  Comp-Off and Overtime are narrowed by the same choice through allowedEmails. */}
              <div className="lr-table-filters">
                <label className="lr-filter-labelled">
                  <span>Employee Type</span>
                  <select
                    className="lr-input"
                    value={employeeType}
                    onChange={e => setEmployeeType(e.target.value as EmployeeTypeFilter)}
                  >
                    {(Object.keys(EMPLOYEE_TYPE_LABELS) as EmployeeTypeFilter[]).map(k => (
                      <option key={k} value={k}>{EMPLOYEE_TYPE_LABELS[k]}</option>
                    ))}
                  </select>
                </label>

                {employeeType === 'SELECTED' && (
                  <div className="lr-emp-picker">
                    <button
                      type="button"
                      className="lr-input lr-emp-picker-toggle"
                      onClick={() => setEmpPickerOpen(o => !o)}
                      aria-expanded={empPickerOpen}
                    >
                      {selectedEmails.length === 0
                        ? 'Choose employees…'
                        : `${selectedEmails.length} employee${selectedEmails.length === 1 ? '' : 's'} selected`}
                    </button>

                    {empPickerOpen && (
                      <div className="lr-emp-picker-panel">
                        <input
                          className="lr-input"
                          type="text"
                          placeholder="🔍 Search by name or email…"
                          value={empPickerSearch}
                          onChange={e => setEmpPickerSearch(e.target.value)}
                          autoFocus
                        />
                        <div className="lr-emp-picker-actions">
                          <button type="button" onClick={() => setSelectedEmails(pickerEmployees.map(u => u.email))}>
                            Select all {empPickerSearch.trim() ? 'matching' : ''}
                          </button>
                          <button type="button" onClick={() => setSelectedEmails([])}>Clear</button>
                        </div>
                        <div className="lr-emp-picker-list">
                          {pickerEmployees.length === 0 ? (
                            <p className="lr-emp-picker-empty">Nobody matches that.</p>
                          ) : pickerEmployees.map(u => {
                            const checked = selectedEmails.some(e => e.toLowerCase() === (u.email || '').toLowerCase());
                            return (
                              <label key={u.email} className="lr-emp-picker-item">
                                <input type="checkbox" checked={checked} onChange={() => toggleSelected(u.email)} />
                                <span>
                                  <strong>{u.fullName || u.name}</strong>
                                  <small>{u.email}{hasLeft(u, todayIso) ? ' · past employee' : ''}</small>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="lr-emp-picker-done"
                          onClick={() => setEmpPickerOpen(false)}
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {(reportType === 'EMP' || reportType === 'BALANCE') && (
                <div className="lr-table-filters">
                  <input className="lr-input" type="text" placeholder="🔍 Search employee…" value={search} onChange={e => setSearch(e.target.value)} />
                  <select className="lr-input" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                    <option value="ALL">All Departments</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {teams.length > 0 && (
                    <select className="lr-input" value={teamFilter} onChange={e => setTeamFilter(e.target.value)}>
                      <option value="ALL">All Teams</option>
                      {teams.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  )}
                  {employmentTypes.length > 0 && (
                    <select className="lr-input" value={employmentFilter} onChange={e => setEmploymentFilter(e.target.value)}>
                      <option value="ALL">All Employment Types</option>
                      {employmentTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  )}
                  <select className="lr-input" value={sortKey} onChange={e => setSortKey(e.target.value as any)}>
                    <option value="used">Sort: Most used</option>
                    <option value="balance">Sort: Highest balance</option>
                    <option value="name">Sort: Name</option>
                  </select>
                </div>
              )}
            </div>

            {(reportType === 'EMP' || reportType === 'BALANCE') && (
              visibleRows.length === 0 ? (
                <EmptyState icon="search" title="No employees match" subtitle="Try a different search term or department." />
              ) : (
                <div className="lr-table-scroll">
                  <table className="lr-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Department</th>
                        {leaveTypes.map(t => <th key={t.code} className="lr-num">{t.name}</th>)}
                        <th className="lr-num">Used</th>
                        <th className="lr-num">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map(r => (
                        <tr key={r.user.email}>
                          <td>
                            <span className="lr-emp">
                              <span className="lr-avatar" aria-hidden="true">{initials(r.user.fullName || r.user.name)}</span>
                              <span className="lr-emp-id">
                                <strong>{r.user.fullName || r.user.name}</strong>
                                <small>{r.user.email}</small>
                              </span>
                            </span>
                          </td>
                          <td><span className="lr-dept-chip">{r.user.department || '—'}</span></td>
                          {leaveTypes.map((t, i) => {
                            const s = r.byType[t.code.toUpperCase()];
                            return (
                              <td key={t.code} className="lr-num">
                                {s ? (
                                  <span
                                    className="lr-cell-pair"
                                    title={
                                      `${t.name}: allotment ${LeaveBalanceService.totalEntitlement(s)}`
                                      + ` (accrued ${s.granted}`
                                      + `${s.compOffEarned ? `, comp-off +${s.compOffEarned}` : ''}`
                                      /* Credits only in the entitlement breakdown. A negative
                                         adjustment is leave already taken, and it is accounted
                                         for in `used` below — printing it here as well would
                                         count the same days twice. */
                                      + `${LeaveBalanceService.hrCreditOf(s) ? `, HR +${LeaveBalanceService.hrCreditOf(s)}` : ''}`
                                      + `), used ${LeaveBalanceService.totalUsed(s)}`
                                      + `${LeaveBalanceService.historicalUsedOf(s) ? ` (incl. ${LeaveBalanceService.historicalUsedOf(s)} before go-live)` : ''}`
                                      + `${s.pending ? `, pending ${s.pending}` : ''}`
                                      + `, balance ${s.balance}`
                                      // The cell itself is too narrow for a date, but HR
                                      // fielding "why did my comp-off vanish" needs the
                                      // deadline somewhere on the row they are already reading.
                                      + `${expirySummary(s.expiringGrants) ? `\n${expiryTooltip(s.expiringGrants)}` : ''}`
                                    }
                                  >
                                    <span className="lr-cell-bal" style={{ color: colorFor(t.code, i) }}>{s.balance}</span>
                                    <span className="lr-cell-of">/ {LeaveBalanceService.totalEntitlement(s)}</span>
                                  </span>
                                ) : '—'}
                              </td>
                            );
                          })}
                          <td className="lr-num"><strong>{round1(r.consumed)}</strong></td>
                          <td className="lr-num">
                            <span className={`lr-pill ${r.balance <= 0 ? 'is-crit' : r.balance < 3 ? 'is-warn' : 'is-good'}`}>
                              {round1(r.balance)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {reportType === 'COMPOFF' && (
              visibleCompOff.length === 0 ? (
                <EmptyState icon="inbox" title="No comp-off records" subtitle="Comp-off grants for weekend or holiday work will appear here." />
              ) : (
                <div className="lr-table-scroll">
                  <table className="lr-table">
                    <thead><tr><th>Employee</th><th>Worked Date</th><th>Holiday?</th><th className="lr-num">Days</th><th>Status</th><th>Expires</th><th>Granted By</th></tr></thead>
                    <tbody>
                      {visibleCompOff.map(c => {
                        /*
                         * Derived from the worked date via expiryDateFor, not read off the
                         * stored expiryDate field — the window is a live HR setting, so the
                         * stored value can be stale, and isExpired deliberately ignores it
                         * for exactly that reason. Reading it here would show HR a deadline
                         * the balance maths does not actually use.
                         */
                        const expiresOn = c.status === 'APPROVED'
                          ? CompOffService.expiryDateFor(c.workedDate)
                          : '';
                        const left = expiresOn ? daysUntilExpiry(expiresOn) : 0;
                        return (
                          <tr key={c.id}>
                            <td>{c.employeeEmail}</td>
                            <td>{c.workedDate}</td>
                            <td>{c.isHoliday ? 'Yes' : 'No'}</td>
                            <td className="lr-num"><strong>{c.daysGranted}</strong></td>
                            <td><span className={`lr-pill ${c.status === 'APPROVED' ? 'is-good' : c.status === 'PENDING' ? 'is-warn' : 'is-crit'}`}>{c.status}</span></td>
                            {/* Only approved days have a usable window to run out — a pending
                                or rejected claim has no days, so a deadline against it is noise. */}
                            <td>
                              {expiresOn ? (
                                <span style={left <= 7 ? { color: '#b45309', fontWeight: 700 } : undefined}>
                                  {formatExpiryDate(expiresOn)}
                                  {left < 0 && ' (lapsed)'}
                                </span>
                              ) : '—'}
                            </td>
                            <td>{c.grantedBy}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {reportType === 'OVERTIME' && (
              visibleOvertime.length === 0 ? (
                <EmptyState icon="inbox" title="No overtime records" subtitle="Approved overtime hours will appear here once logged." />
              ) : (
                <div className="lr-table-scroll">
                  <table className="lr-table">
                    <thead><tr><th>Employee</th><th>Date</th><th>OT Type</th><th className="lr-num">Hours</th><th>Status</th></tr></thead>
                    <tbody>
                      {visibleOvertime.map(o => (
                        <tr key={o.id}>
                          <td>{o.employeeEmail}</td>
                          <td>{o.date}</td>
                          <td>{o.otType}</td>
                          <td className="lr-num"><strong>{o.hoursWorked} hrs</strong></td>
                          <td><span className={`lr-pill ${o.status === 'APPROVED' ? 'is-good' : o.status === 'PENDING' ? 'is-warn' : 'is-crit'}`}>{o.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default LeaveReportsPage;
